import { Add as AddIcon, Delete as DeleteIcon, InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { Box, Button, IconButton } from "@mui/material";
import { GridColDef, GridPaginationModel, GridRenderCellParams } from "@mui/x-data-grid";
import { FC, useMemo, useState } from "react";

import { AGDataGrid } from "../../components/AGDataGrid";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { useNotification } from "../../components/FeedbackNotification/useNotification";
import TableSearchInput from "../../components/TableSearchInput";
import { useAppSelector } from "../../redux";
import {
	useDeleteJurisdictionMutation,
	useGetJurisdictionsListQuery
} from "../../redux/reducers/jurisdictions.reducer";
import { IGetJurisdictionsListRequestData, JURISDICTION_TYPE_E } from "../../types";
import { ROWS_PER_PAGE } from "../../utils/constants";
import JurisdictionDetailsDialog from "./JurisdictionDetailsDialog";
import "./index.css";

export interface IRTODetails {
	id: number;
	sr_no: number;
	name: string;
	rto_code: string;
	description: string;
	jurisdiction: number;
}

export interface IJurisdictionDetails {
	id: number;
	sr_no: number;
	name: string;
	description: string;
	rto_codes: string[];
}

const Jurisdictions: FC = () => {
	const { notify } = useNotification();

	const authState = useAppSelector((state) => state.auth);

	const [filters, setFilters] = useState<IGetJurisdictionsListRequestData>({
		page: 1,
		jurisdiction_type: JURISDICTION_TYPE_E.JURISDICTION
	});
	const [jurisdictionDetailsDialogOpen, setJurisdictionDetailsDialogOpen] = useState<boolean>(false);
	const [selectedJurisdictionDetails, setSelectedJurisdictionDetails] = useState<IJurisdictionDetails | null>(null);
	const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState<boolean>(false);

	// APIS
	// GET JURISDICTIONS LIST
	const {
		data: getJurisdictionsListResponse,
		isLoading: getJurisdictionsListLoading,
		isFetching: getJurisdictionsListFetching,
		refetch: refetchJurisdictionsList
	} = useGetJurisdictionsListQuery(filters);

	// DELETE JURISDICTION
	const [deleteJurisdiction, { isLoading: deleteJurisdictionLoading, reset: resetDeleteJurisdictionState }] =
		useDeleteJurisdictionMutation();

	const readOnlyPermission = useMemo<boolean>(
		() => !authState.user.permissions || !authState.user.permissions.rto_metadata?.edit,
		[authState.user.permissions]
	);

	const rowsData = useMemo<IJurisdictionDetails[]>(() => {
		if (!getJurisdictionsListResponse) return [];

		return getJurisdictionsListResponse.results.map((item, index) => ({
			id: item.id,
			sr_no: ((filters.page || 1) - 1) * ROWS_PER_PAGE + index + 1,
			name: item.name,
			description: item.description ?? "",
			rto_codes: item.descendants.map((rto) => rto.rto_code ?? rto.name)
		}));
	}, [filters.page, getJurisdictionsListResponse]);

	function handleSubmitSearchInput(searchInput: string) {
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (searchInput) updatedFilters.search = searchInput;
			else delete updatedFilters.search;

			return updatedFilters;
		});
	}

	function handleOpenJurisdictionDialog(jurisdictionDetails: IJurisdictionDetails | null) {
		setSelectedJurisdictionDetails(jurisdictionDetails);
		setJurisdictionDetailsDialogOpen(true);
	}

	function handleCloseJurisdictionDialog() {
		setJurisdictionDetailsDialogOpen(false);
		refetchJurisdictionsList();
	}

	function handlePageChange(event: GridPaginationModel) {
		setFilters((prevFilters) => ({
			...prevFilters,
			page: event.page + 1
		}));
	}

	function openDeleteConfirmationDialog(jurisdictionDetails: IJurisdictionDetails) {
		setSelectedJurisdictionDetails(jurisdictionDetails);
		setDeleteConfirmationDialogOpen(true);
	}

	function closeDeleteConfirmationDialog() {
		setDeleteConfirmationDialogOpen(false);
		setSelectedJurisdictionDetails(null);
	}

	function handleDeleteJurisdiction() {
		if (!selectedJurisdictionDetails) return;

		deleteJurisdiction(selectedJurisdictionDetails.id)
			.unwrap()
			.then(() => {
				closeDeleteConfirmationDialog();
				refetchJurisdictionsList();
			})
			.catch((error) => {
				notify("data" in error && error.data ? String(error.data) : "Failed to delete jurisdiction", "error");
				closeDeleteConfirmationDialog();
			})
			.finally(() => {
				resetDeleteJurisdictionState();
			});
	}

	const columnsData: GridColDef[] = [
		{
			field: "sr_no",
			headerName: "Sr. No.",
			width: 75,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "name",
			headerName: "Jurisdiction Name",
			flex: 1,
			minWidth: 150,
			sortable: false
		},
		{
			field: "description",
			headerName: "Description",
			flex: 2,
			minWidth: 200,
			sortable: false
		},
		{
			field: "rto_codes",
			headerName: "RTOs",
			flex: 2,
			minWidth: 200,
			sortable: false,
			valueFormatter: (value: string[]) => value.join(", ")
		},
		{
			field: "actions",
			headerName: "",
			width: readOnlyPermission ? 50 : 75,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IJurisdictionDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					{!readOnlyPermission ? (
						<IconButton color="error" size="small" onClick={() => openDeleteConfirmationDialog(params.row)}>
							<DeleteIcon fontSize="inherit" />
						</IconButton>
					) : null}

					<IconButton color="info" size="small" onClick={() => handleOpenJurisdictionDialog(params.row)}>
						<InfoOutlinedIcon fontSize="inherit" />
					</IconButton>
				</Box>
			)
		}
	];

	return (
		<Box className="page-content-wrapper">
			<Box className="page-header-wrapper">
				<TableSearchInput showClearButton={!!filters.search} onSubmit={handleSubmitSearchInput} />

				{!readOnlyPermission ? (
					<Button
						disableElevation
						variant="contained"
						color="primary"
						startIcon={<AddIcon />}
						onClick={() => handleOpenJurisdictionDialog(null)}
					>
						Add Jurisdiction
					</Button>
				) : null}
			</Box>

			<Box className="page-content">
				<AGDataGrid
					hideLastSeparator
					columns={columnsData}
					rows={rowsData}
					paginationMode="server"
					paginationModel={{ page: (filters.page || 1) - 1, pageSize: ROWS_PER_PAGE }}
					onPaginationModelChange={handlePageChange}
					rowCount={getJurisdictionsListResponse?.count ?? 0}
					loading={getJurisdictionsListLoading || getJurisdictionsListFetching}
					hideFooter={(getJurisdictionsListResponse?.count ?? 0) <= ROWS_PER_PAGE}
				/>
			</Box>

			<JurisdictionDetailsDialog
				open={jurisdictionDetailsDialogOpen}
				onClose={handleCloseJurisdictionDialog}
				jurisdictionDetails={selectedJurisdictionDetails}
			/>

			<ConfirmationDialog
				open={deleteConfirmationDialogOpen}
				title="Delete Jurisdiction"
				heading="Are you sure you want to delete this jurisdiction?"
				onClose={closeDeleteConfirmationDialog}
				onConfirm={handleDeleteJurisdiction}
				color="error"
				confirmButtonLoading={deleteJurisdictionLoading}
				submitButtonText="Delete"
				width="xs"
			/>
		</Box>
	);
};

export default Jurisdictions;
